// Published methodology constants (SPEC §2.5). Imported by the Trust Score
// implementation AND by /methodology, so the page cannot drift from the formula.

export const TRUST_SCORE_VERSION = "1.1.0";
export const MIN_REVIEWS_TO_SCORE = 3;
export const MIN_COMPONENTS_TO_SCORE = 3;

export interface TrustWeight {
  key: string;
  label: string;
  weight: number;
  description: string;
}

export const TRUST_SCORE_WEIGHTS: TrustWeight[] = [
  { key: "verified_share", label: "Verified review share", weight: 0.26,
    description: "Proportion of reviews backed by cryptographic proof of usage." },
  { key: "rating", label: "Reputation-weighted rating", weight: 0.22,
    description: "The weighted rating, normalised to a 0-1 scale." },
  { key: "volume", label: "Review volume", weight: 0.13,
    description: "How many published reviews exist, saturating at 50." },
  { key: "recency", label: "Recency", weight: 0.09,
    description: "Share of reviews written in the last 180 days." },
  { key: "age", label: "Entity age", weight: 0.09,
    description: "Months since the contract deployed or the listing was created, saturating at 24." },
  { key: "market_depth", label: "Market depth", weight: 0.09,
    description: "Log-scaled market capitalisation. Tokens only." },
  { key: "security", label: "Security posture", weight: 0.12,
    description: "Scanned contract risk: honeypot behaviour, mint authority, pausable transfers, reclaimable ownership. Absent when a scan has not succeeded." },
];

export interface MethodologyChange {
  version: string;
  date: string;
  summary: string;
}

// Re-weighting moves every score on the site. That is a published change, not a
// deployment detail, so it is recorded here and rendered on /methodology.
export const TRUST_SCORE_CHANGELOG: MethodologyChange[] = [
  {
    version: "1.1.0",
    date: "2026-09-02",
    summary:
      "Added a security-posture component from scanned contract flags, and began preferring on-chain contract deployment date for entity age. The other six weights were reduced proportionally to make room. Scores moved as a result.",
  },
  { version: "1.0.0", date: "2026-09-01", summary: "First published Trust Score." },
];

export const REPUTATION_DELTAS_PUBLISHED: { kind: string; delta: number; label: string }[] = [
  { kind: "review_published", delta: 2, label: "A review of yours is published" },
  { kind: "review_verified", delta: 5, label: "That review carried proof of usage" },
  { kind: "helpful_vote_received", delta: 0.5, label: "Someone marks your review helpful (capped at 20 per review)" },
  { kind: "unhelpful_vote_received", delta: -0.25, label: "Someone marks your review unhelpful" },
  { kind: "review_removed_tos", delta: -10, label: "A review of yours is removed for a policy violation" },
  { kind: "flag_upheld", delta: 3, label: "You flag a review and moderation agrees" },
  { kind: "flag_rejected", delta: -1, label: "You flag a review and moderation disagrees" },
];
